import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import type { Question } from '../types';

interface QuestionFormData {
  text: string;
  options: string[];
  correctAnswer: string;
  hints: string[];
  category: string;
  points: number;
}

const emptyQuestion: QuestionFormData = {
  text: '',
  options: ['', '', '', ''],
  correctAnswer: '',
  hints: ['', ''],
  category: '',
  points: 10
};

export default function QuestionManager() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>(emptyQuestion);

  // Load questions
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/questions`);
      const data = await response.json();
      setQuestions(data);
    } catch (err) {
      setError('Kon vragen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (!formData.text.trim()) {
      setError('Vraag tekst is verplicht');
      return;
    }
    if (formData.options.some(o => !o.trim())) {
      setError('Alle 4 antwoordopties zijn verplicht');
      return;
    }
    if (!formData.correctAnswer) {
      setError('Selecteer het juiste antwoord');
      return;
    }

    try {
      const url = editingId 
        ? `${API_URL}/api/questions/${editingId}`
        : `${API_URL}/api/questions`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          hints: formData.hints.filter(h => h.trim())
        })
      });

      if (response.ok) {
        fetchQuestions();
        resetForm();
      }
    } catch (err) {
      setError('Kon vraag niet opslaan');
    }
  };

  const handleEdit = (question: Question) => {
    setFormData({
      text: question.text,
      options: question.options,
      correctAnswer: question.correctAnswer,
      hints: question.hints.length >= 2 ? question.hints : [...question.hints, ''],
      category: question.category || '',
      points: question.points
    });
    setEditingId(question.id);
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Weet je zeker dat je deze vraag wilt verwijderen?')) {
      return;
    }

    try {
      await fetch(`${API_URL}/api/questions/${id}`, {
        method: 'DELETE'
      });
      fetchQuestions();
    } catch (err) {
      setError('Kon vraag niet verwijderen');
    }
  };

  const resetForm = () => {
    setFormData(emptyQuestion);
    setEditingId(null);
    setIsEditing(false);
    setError(null);
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const updateHint = (index: number, value: string) => {
    const newHints = [...formData.hints];
    newHints[index] = value;
    setFormData({ ...formData, hints: newHints });
  };

  if (loading) {
    return (
      <div className="container flex-center" style={{ minHeight: '100vh' }}>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '20px', paddingBottom: '40px' }}>
      {/* Header */}
      <div className="flex-between mb-3">
        <div>
          <h1 style={{ color: 'var(--color-primary)' }}>📝 Vragen Beheren</h1>
          <p className="text-muted">{questions.length} vragen in de bibliotheek</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          ← Terug naar Admin
        </button>
      </div>

      {error && (
        <div className="card mb-2 animate-shake" style={{ background: 'var(--color-error)', padding: '12px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div className="grid grid-2 gap-3">
        {/* Left: Form */}
        <div>
          <div className="card">
            <h3 className="mb-2">{editingId ? '✏️ Vraag Bewerken' : '➕ Nieuwe Vraag'}</h3>
            
            <form onSubmit={handleSubmit}>
              {/* Question text */}
              <div className="mb-2">
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Vraag *
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, text: e.target.value })}
                  placeholder="Typ hier je vraag..."
                  rows={3}
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '2px solid var(--color-secondary)',
                    background: 'var(--color-bg)',
                    color: 'white',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Options */}
              <div className="mb-2">
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Antwoordopties * (klik op het juiste antwoord)
                </label>
                {formData.options.map((option, index) => (
                  <div key={index} className="flex gap-1 mb-1">
                    <button
                      type={"button" as const}
                      onClick={() => setFormData({ ...formData, correctAnswer: option })}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: formData.correctAnswer === option && option ? 'var(--color-success)' : 'var(--color-secondary)',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {formData.correctAnswer === option && option ? '✓' : String.fromCharCode(65 + index)}
                    </button>
                    <input
                      type="text"
                      value={option}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOption(index, e.target.value)}
                      placeholder={`Optie ${String.fromCharCode(65 + index)}`}
                      style={{ flex: 1 }}
                    />
                  </div>
                ))}
              </div>

              {/* Hints */}
              <div className="mb-2">
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                  Hints (voor sabotage)
                </label>
                {formData.hints.map((hint, index) => (
                  <input
                    key={index}
                    type="text"
                    value={hint}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateHint(index, e.target.value)}
                    placeholder={`Hint ${index + 1}`}
                    className="mb-1"
                  />
                ))}
              </div>

              {/* Category & Points */}
              <div className="flex gap-2 mb-2">
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                    Categorie
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Bijv. Sport, Geschiedenis"
                  />
                </div>
                <div style={{ width: '100px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}>
                    Punten
                  </label>
                  <select
                    value={formData.points}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, points: Number(e.target.value) })}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-1">
                <button type={"submit" as const} className="btn btn-primary" style={{ flex: 1 }}>
                  {editingId ? '💾 Opslaan' : '➕ Toevoegen'}
                </button>
                {editingId && (
                  <button type={"button" as const} className="btn btn-secondary" onClick={resetForm}>
                    Annuleren
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right: Question list */}
        <div>
          <div className="card">
            <h3 className="mb-2">📋 Vragenlijst</h3>
            
            {questions.length === 0 ? (
              <p className="text-muted">Nog geen vragen. Voeg je eerste vraag toe!</p>
            ) : (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {questions.map((question, index) => (
                  <div 
                    key={question.id}
                    className="card mb-1"
                    style={{ 
                      background: 'var(--color-secondary)', 
                      padding: '12px',
                      borderLeft: editingId === question.id ? '4px solid var(--color-primary)' : 'none'
                    }}
                  >
                    <div className="flex-between mb-1">
                      <span style={{ fontWeight: 600 }}>
                        #{index + 1} {question.category && <span className="text-muted">({question.category})</span>}
                      </span>
                      <span className="score-badge">{question.points} pts</span>
                    </div>
                    <p style={{ marginBottom: '8px' }}>{question.text}</p>
                    <div className="flex gap-1" style={{ fontSize: '0.875rem' }}>
                      {question.options.map((opt, i) => (
                        <span 
                          key={i}
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: opt === question.correctAnswer ? 'var(--color-success)' : 'var(--color-bg)'
                          }}
                        >
                          {opt}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '4px 12px', fontSize: '0.875rem' }}
                        onClick={() => handleEdit(question)}
                      >
                        ✏️ Bewerken
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '4px 12px', fontSize: '0.875rem', background: 'var(--color-error)' }}
                        onClick={() => handleDelete(question.id)}
                      >
                        🗑️ Verwijderen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
